import { AppThunk } from "../app/store";
import {
  searchAsins,
  getEditableAttributes,
  prepareRecordAsinMarketplaceDatesWithAsins,
  recordAsinMarketplaceDatesWithAsins,
  prepareSetLaunchAttributesWithAsins,
  setLaunchAttributesWithAsins,
} from "../app/api";
import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../from-server/asin-central/model";
import { SearchResult, SortFacet } from "../../from-server/asin-central/search";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthorizedAttribute } from "../../from-server/brass/model";
import {
  porKey,
  RESTRICTED_SEARCH_MESSAGE_ITEMS,
  SET_ASIN_MARKETPLACE_ATTRIBUTES_SUCCESS_ITEMS,
  SET_ASIN_MARKETPLACE_DATES_SUCCESS_ITEMS,
} from "./PorAsinListTable-config";
import {
  convertDateStringToUTCDate,
  getPstOffset,
  getRelativeDate,
} from "../../util/dateUtils";
import {
  addNotificationWithKey,
  clearNotifications,
  setNotification,
} from "../../features/notification/notificationConnector";
import { IN_PROGRESS_NOTIF_ITEMS } from "../notification/notification-config";
import { ERROR_NOTIF_ITEMS } from "../notification/notification-config";
import moment from "moment/moment";

interface PorAsinListState {
  currentPagePorAsins: Array<FullAsinWithAudits | RestrictedAsin>;
  count: number;
  isLoading: boolean;
  error: string | null;
  isEditing: boolean;
  porIndexMap: { [asinMp: string]: number };
  edited: boolean;
  editedRowSet: { [index: number]: boolean };
  editablePorAsins: Array<FullAsinWithAudits | RestrictedAsin>;
  refresh: boolean;
  restrictedPrograms: string[];
  editableAttributes: AuthorizedAttribute[];
  isGettingEditableAttributes: boolean;
}

const porAsinListInitialState: PorAsinListState = {
  currentPagePorAsins: [],
  count: 0,
  isLoading: false,
  error: null,
  isEditing: false,
  porIndexMap: {},
  edited: false,
  editedRowSet: {},
  editablePorAsins: [],
  refresh: false,
  restrictedPrograms: [],
  editableAttributes: [],
  isGettingEditableAttributes: false,
};

function updateEditedEntry(
  _state: PorAsinListState,
  attribute: "announceDate" | "onlineStreetDate" | "userDescription",
  value: string,
  entryToUpdate: FullAsinWithAudits
): FullAsinWithAudits {
  const updatedEntry = { ...entryToUpdate };
  if (attribute !== "userDescription") {
    // value is expected to have no offset, so we add the PST offset here before updating.
    const dateVal =
      value.trim().length === 0 ? undefined : convertDateStringToUTCDate(value);
    const pstOffSet = dateVal ? getPstOffset(dateVal) : 0;
    const updatedDate = dateVal
      ? getRelativeDate(dateVal, -pstOffSet, "minute")
      : undefined;
    updatedEntry[attribute] = updatedDate;
  } else {
    updatedEntry[attribute] = value.trim();
  }
  return updatedEntry;
}

const porAsinList = createSlice({
  name: "porAsinList",
  initialState: porAsinListInitialState,
  reducers: {
    getEditableAttributesStart(state: PorAsinListState) {
      state.isGettingEditableAttributes = true;
    },
    getEditableAttributesSuccess(state: PorAsinListState, { payload }) {
      state.editableAttributes = payload;
    },
    searchPorAsinStart(state: PorAsinListState) {
      state.isLoading = true;
    },
    searchPorAsinSuccess(
      state: PorAsinListState,
      { payload }: PayloadAction<SearchResult>
    ) {
      state.currentPagePorAsins = payload.asins;
      state.count = payload.totalResultCount || 0;
      state.isLoading = false;
      // state.restrictedPrograms = payload.restrictedPrograms
    },
    searchPorAsinFailure(
      state: PorAsinListState,
      action: PayloadAction<string>
    ) {
      state.isLoading = false;
      state.error = action.payload;
    },
    setEditing(state) {
      state.isEditing = true;
      state.editedRowSet = {};
      state.editablePorAsins = [...state.currentPagePorAsins];
      state.editablePorAsins.forEach((value, index) => {
        state.porIndexMap[porKey(value)] = index;
      });
    },
    setViewing(state) {
      state.isEditing = false;
      state.porIndexMap = {};
      state.edited = false;
      state.editablePorAsins = [];
    },
    updatePorAsin(state, action) {
      const [entryToUpdate, attribute, value] = action.payload;
      const updatedEntry = updateEditedEntry(
        state,
        attribute,
        value,
        entryToUpdate
      );
      state.editedRowSet[state.porIndexMap[porKey(entryToUpdate)]] = true;
      state.editablePorAsins[state.porIndexMap[porKey(entryToUpdate)]] =
        updatedEntry;
      state.edited = true;
    },
    bulkUpdatePorAsins(state, action) {
      const [attribute, value] = action.payload;
      state.editablePorAsins = state.editablePorAsins.map((entryToUpdate) => {
        if (!entryToUpdate.isRestricted) {
          state.editedRowSet[state.porIndexMap[porKey(entryToUpdate)]] = true;
          return updateEditedEntry(state, attribute, value, entryToUpdate);
        }
        return entryToUpdate;
      });
      state.edited = Object.keys(state.editedRowSet).length > 0;
    },
    setPorAttributesSuccess(state) {
      state.refresh = !state.refresh;
    },
    setPorAttributesFailure(state) {
      state.refresh = !state.refresh;
    },
  },
});

export const {
  getEditableAttributesStart,
  getEditableAttributesSuccess,
  searchPorAsinStart,
  searchPorAsinSuccess,
  searchPorAsinFailure,
  setEditing,
  setViewing,
  updatePorAsin,
  bulkUpdatePorAsins,
  setPorAttributesSuccess,
  setPorAttributesFailure,
} = porAsinList.actions;

export default porAsinList.reducer;

export const fetchPorAsins =
  (
    offset: number,
    pageSize: number,
    queryString: string,
    sortFacets: SortFacet[],
    clearNotificationsBoolean: boolean = true
  ): AppThunk =>
  async (dispatch) => {
    try {
      if (clearNotificationsBoolean) {
        dispatch(clearNotifications());
      }
      dispatch(searchPorAsinStart());
      const porAsinPage = await searchAsins(
        offset,
        pageSize,
        queryString,
        sortFacets
      );
      dispatch(searchPorAsinSuccess(porAsinPage));
      const restrictedAsins = porAsinPage.asins.filter(
        (value) => "isRestricted" in value && value.isRestricted
      );
      if (restrictedAsins.length > 0) {
        const programNames = restrictedAsins.map(
          (value) => (value as RestrictedAsin).restrictedProgramName ?? ""
        );
        dispatch(
          setNotification(RESTRICTED_SEARCH_MESSAGE_ITEMS(programNames))
        );
      }
    } catch (err: any) {
      dispatch(searchPorAsinFailure(err.toString()));
      dispatch(setNotification(ERROR_NOTIF_ITEMS(err.toString())));
    }
  };

export const setUpdatedEntries =
  (
    currentPagePorAsins: (FullAsinWithAudits | RestrictedAsin)[],
    updatedEntries: (FullAsinWithAudits | RestrictedAsin)[],
    editedRowsSet: { [index: number]: boolean }
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(clearNotifications());
      const { asinMarketplaceDates, dateTypes } =
        prepareRecordAsinMarketplaceDatesWithAsins(
          currentPagePorAsins as FullAsinWithAudits[],
          updatedEntries as FullAsinWithAudits[],
          editedRowsSet
        );
      if (asinMarketplaceDates.length !== 0) {
        const messageNotificationKeyMarketplace = moment.now();
        dispatch(
          addNotificationWithKey({
            key: messageNotificationKeyMarketplace,
            message: IN_PROGRESS_NOTIF_ITEMS("Updating POR ASINs Dates"),
          })
        );
        await recordAsinMarketplaceDatesWithAsins(
          asinMarketplaceDates,
          dateTypes
        );
        dispatch(
          addNotificationWithKey({
            key: messageNotificationKeyMarketplace,
            message:
              SET_ASIN_MARKETPLACE_DATES_SUCCESS_ITEMS(asinMarketplaceDates),
          })
        );
      }

      const asinMarketplaceAttributes = prepareSetLaunchAttributesWithAsins(
        currentPagePorAsins as FullAsinWithAudits[],
        updatedEntries as FullAsinWithAudits[],
        editedRowsSet
      );
      if (asinMarketplaceAttributes.length !== 0) {
        const messageNotificationKeyAttributes = moment.now();
        dispatch(
          addNotificationWithKey({
            key: messageNotificationKeyAttributes,
            message: IN_PROGRESS_NOTIF_ITEMS("Updating POR ASINs Attributes"),
          })
        );
        await setLaunchAttributesWithAsins(asinMarketplaceAttributes);
        dispatch(
          addNotificationWithKey({
            key: messageNotificationKeyAttributes,
            message: SET_ASIN_MARKETPLACE_ATTRIBUTES_SUCCESS_ITEMS(
              asinMarketplaceAttributes
            ),
          })
        );
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      dispatch(setNotification(ERROR_NOTIF_ITEMS(errorMessage)));
      dispatch(setPorAttributesFailure());
    }
  };

export const fetchEditableAttributes = (): AppThunk => async (dispatch) => {
  dispatch(getEditableAttributesStart());
  const attributes = await getEditableAttributes();
  if (!Array.isArray(attributes)) {
    //todo: errors
    console.error("failed to fetch editable attributes");
  } else {
    dispatch(getEditableAttributesSuccess(attributes));
  }
};
